import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Building2, Sparkles, Clock, DollarSign, Award, ArrowRight,
  Check, ArrowLeft,
} from "lucide-react";
import { LOGO_URL } from "@/byrd/data";

/**
 * Public landing page for commercial listing agents. Pitches the Loan Quote
 * Studio as a free marketing tool and captures a lead (name, company, email,
 * phone, market). Submission fires POST /public/listing-agents/interest which
 * creates a Contact tagged listing_agent in the CRM and pings every broker.
 */
export default function ListingAgentsLanding() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    name: "", company: "", email: "", phone: "", market: "",
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.name.trim() || !f.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    // Basic email sanity — server enforces via Pydantic EmailStr anyway
    if (!/.+@.+\..+/.test(f.email)) {
      toast.error("That email doesn't look right.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/listing-agents/interest", {
        name: f.name.trim(),
        company: f.company.trim(),
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim(),
        market: f.market.trim(),
      });
      setDone(true);
      // Bring the thank-you screen into view on mobile
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went sideways — try again.";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F1] text-[#1A1A1A]" data-testid="listing-agents-landing">
      {/* Slim header — matches the marketing site vibe without pulling in nav */}
      <header className="border-b border-[#E4DFD1] bg-white/60 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="landing-logo">
            <img src={LOGO_URL} alt="Byrd & CO" className="h-8 w-8" />
            <div className="leading-tight">
              <div className="font-serif text-lg font-bold">Byrd &amp; CO</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#6B6558]">
                Commercial Real Estate Debt
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-[11px] font-mono uppercase tracking-widest text-[#6B6558] hover:text-[#1A1A1A] inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14 md:py-20">
        {/* Eyebrow + Headline */}
        <div className="max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C89434] mb-4">
            // For Commercial Listing Agents
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Give Your Commercial Listings a Financing Advantage.
          </h1>
          <p className="text-base md:text-lg text-[#3D3A32] mt-6 max-w-2xl leading-relaxed">
            Create a professional, broker-branded debt quote for any commercial listing in minutes —
            <span className="font-semibold text-[#C89434]"> free</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 mt-12 items-start">
          {/* LEFT — video + benefits */}
          <div className="lg:col-span-3 space-y-10">
            {/* YouTube demo */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-2">
                // 90-second demo
              </div>
              <div className="relative w-full rounded-lg overflow-hidden border border-[#E4DFD1] shadow-sm bg-black" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/KF35xG0n0Jw"
                  title="Byrd & CO Loan Quote Studio Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="landing-demo-video"
                />
              </div>
            </div>

            {/* Benefits */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-4">
                // Why listing agents use it
              </div>
              <ul className="grid sm:grid-cols-2 gap-4">
                <BenefitCard
                  icon={<Award size={16} />}
                  title="Better marketing material"
                  body="Every listing gets a polished, broker-branded debt quote you can hand to serious buyers."
                />
                <BenefitCard
                  icon={<DollarSign size={16} />}
                  title="Financing reference at first look"
                  body="Buyers see an actual quote — not a napkin estimate — the moment they show interest."
                />
                <BenefitCard
                  icon={<Sparkles size={16} />}
                  title="Look sharper than the field"
                  body="Show up with financing already scoped. It positions you as the sophisticated broker in the room."
                />
                <BenefitCard
                  icon={<Clock size={16} />}
                  title="Minutes, not days"
                  body="No spreadsheets, no calls, no cost. Fill in the property, we do the rest."
                />
              </ul>
              <div className="mt-6 text-[13px] text-[#6B6558] italic">
                Costs the agent nothing. Takes very little time. Free forever for professional listing agents.
              </div>
            </div>
          </div>

          {/* RIGHT — lead form (or thank-you) */}
          <div className="lg:col-span-2 lg:sticky lg:top-6">
            {done ? (
              <div
                className="byrd-card p-8 border border-[#C89434]/40 bg-white"
                data-testid="landing-thanks"
              >
                <div className="w-12 h-12 rounded-full bg-[#F3EEE0] grid place-items-center text-[#C89434] mb-5">
                  <Check size={20} />
                </div>
                <h3 className="font-serif text-2xl font-bold leading-tight">
                  You're on the list.
                </h3>
                <p className="text-[13px] text-[#3D3A32] mt-3 leading-relaxed">
                  Wayne Byrd will reach out shortly to set up your free Quote Studio
                  access. In the meantime, drop him a note anytime at{" "}
                  <a href="mailto:wayne@byrd-co.com" className="text-[#C89434] hover:underline">
                    wayne@byrd-co.com
                  </a>
                  .
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#6B6558] hover:text-[#1A1A1A]"
                >
                  <ArrowLeft size={12} /> Back to Home
                </Link>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="byrd-card p-6 md:p-7 border border-[#E4DFD1] bg-white shadow-sm"
                data-testid="landing-form"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={14} className="text-[#C89434]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">
                    // Get Free Broker Access
                  </div>
                </div>
                <h3 className="font-serif text-2xl font-bold leading-tight mt-1">
                  Two-minute signup.
                </h3>
                <p className="text-[12px] text-[#6B6558] mt-1.5">
                  We'll set you up personally and walk you through your first quote.
                </p>

                <div className="space-y-4 mt-5">
                  <FormField label="Your Name" required>
                    <input
                      value={f.name}
                      onChange={(e) => set("name", e.target.value)}
                      className="byrd-input"
                      placeholder="Jane Smith"
                      autoComplete="name"
                      data-testid="landing-input-name"
                      required
                    />
                  </FormField>
                  <FormField label="Company / Brokerage">
                    <input
                      value={f.company}
                      onChange={(e) => set("company", e.target.value)}
                      className="byrd-input"
                      placeholder="Colliers, CBRE, JLL, Boutique CRE, etc."
                      autoComplete="organization"
                      data-testid="landing-input-company"
                    />
                  </FormField>
                  <FormField label="Email" required>
                    <input
                      type="email"
                      value={f.email}
                      onChange={(e) => set("email", e.target.value)}
                      className="byrd-input"
                      placeholder="you@brokerage.com"
                      autoComplete="email"
                      data-testid="landing-input-email"
                      required
                    />
                  </FormField>
                  <FormField label="Phone">
                    <input
                      type="tel"
                      value={f.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      className="byrd-input"
                      placeholder="(555) 555-5555"
                      autoComplete="tel"
                      data-testid="landing-input-phone"
                    />
                  </FormField>
                  <FormField label="Primary Market">
                    <input
                      value={f.market}
                      onChange={(e) => set("market", e.target.value)}
                      className="byrd-input"
                      placeholder="Houston, TX  ·  Southeast Multifamily  ·  National"
                      data-testid="landing-input-market"
                    />
                  </FormField>
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  className="byrd-btn byrd-btn-dark w-full mt-6 h-12 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="landing-submit"
                >
                  {busy ? "Sending…" : (<>Get Free Broker Access <ArrowRight size={14} /></>)}
                </button>
                <p className="text-[10px] text-[#6B6558] text-center mt-3 leading-relaxed">
                  No credit card. No obligation. Just faster quotes for your listings.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="border-t border-[#E4DFD1] bg-white/50 mt-10">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">
            © {new Date().getFullYear()} Byrd &amp; CO Commercial Real Estate Debt
          </div>
          <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-widest">
            <Link to="/" className="text-[#6B6558] hover:text-[#1A1A1A]">Home</Link>
            <Link to="/privacy" className="text-[#6B6558] hover:text-[#1A1A1A]">Privacy</Link>
            <a href="mailto:wayne@byrd-co.com" className="text-[#6B6558] hover:text-[#1A1A1A]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}


function BenefitCard({ icon, title, body }) {
  return (
    <li className="p-4 rounded-md border border-[#E4DFD1] bg-white/80">
      <div className="flex items-center gap-2 text-[#C89434] mb-1.5">
        {icon}
        <div className="font-serif font-bold text-sm text-[#1A1A1A]">{title}</div>
      </div>
      <div className="text-[12.5px] text-[#3D3A32] leading-relaxed">{body}</div>
    </li>
  );
}


function FormField({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">
        {label}{required ? " *" : ""}
      </div>
      {children}
    </label>
  );
}
